import { Grid, TextField } from 'ui';
import { LegacyPlaceholderBusinessImage } from '@tock/images';
import { Typography } from 'omakase';
import { consumer, core } from '@tock/coretypes';
import { debounce } from 'es-toolkit/compat';
import { getBusinessThumbnailSrc } from 'utils/businessUtils';
import { makeStyles } from '@material-ui/core/styles';
import { useActions } from '@tock/shared/utils/connect';
import { useDidUpdateEffect } from '@tock/hooks';
import React, { useCallback, useMemo, useState } from 'react';
import ThumbImage from 'components/images/ThumbImage';
import pluralize from 'pluralize';

interface Props {
  business: core.IBusiness;
  playlist: consumer.Playlist;
  playlistItem: consumer.IPlaylistItem;
}

const useStyles = makeStyles((theme) => ({
  headerContentContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  headerContent: {
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(2),
    },
  },
}));

const BusinessPlaylistsDialogNotesPage: React.FunctionComponent<Props> = ({
  business,
  playlist,
  playlistItem,
}) => {
  const styles = useStyles();
  const [noteInternal, setNoteInternal] = useState(playlistItem.note || '');
  const [noteError, setNoteError] = useState<string | undefined>();
  const { actions } = useActions();
  const handleChange = useCallback(
    (note: string) => {
      if (note.length <= 255) {
        actions.patron.upsertPlaylistItem(playlist.id, {
          businessId: business.id,
          note,
          id: playlistItem.id,
        });
        setNoteError(undefined);
      } else {
        setNoteError('Max note length is 255');
      }
    },
    [actions.patron, business.id, playlist.id, playlistItem.id]
  );
  const debouncedOnChange = useMemo(() => debounce(handleChange, 600), [handleChange]);

  const handleChangeInternal = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNoteInternal(event.target.value);
  };

  const updateEffect = useCallback(() => {
    debouncedOnChange(noteInternal);
  }, [noteInternal, debouncedOnChange]);

  useDidUpdateEffect(updateEffect, [noteInternal]);

  const imgSrc =
    getBusinessThumbnailSrc(business, { height: 146, width: 260 }) ||
    LegacyPlaceholderBusinessImage;

  return (
    <Grid container spacing={2}>
      <Grid item xs={2}>
        <ThumbImage imgSrc={imgSrc} ariaLabel={business.name || 'business-image'} forceSquare />
      </Grid>
      <Grid item xs={10} className={styles.headerContentContainer}>
        <div className={styles.headerContent}>
          <div>
            <Typography component="h4" variant="body01Strong">
              {business.name}
            </Typography>
          </div>
          <div>
            <Typography variant="body02" color="fgSubtle">
              {playlist.name}
              &ensp;•&ensp;
              {playlist.isPublic ? 'Public' : 'Private'}
              &ensp;•&ensp;
              {playlist.items.length} {pluralize('place', playlist.items.length)}
            </Typography>
          </div>
        </div>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          id="note"
          label="Note"
          placeholder="Add a note about this place?"
          value={noteInternal}
          onChange={handleChangeInternal}
          multiline
          rows={4}
          autoFocus
          error={!!noteError}
          helperText={noteError}
        />
      </Grid>
    </Grid>
  );
};

export default BusinessPlaylistsDialogNotesPage;
